import { Clock, Search, X } from 'lucide-react'
import type { FormEvent, ReactNode } from 'react'

import type { ActiveDay } from './ActiveDays'
import { Button } from './Button'
import { ChoiceChips, type ChoiceChip } from './ChoiceChips'
import { DayStrip, type DayRange } from './DayStrip'
import { SelectField, TextField } from './Fields'
import { MonthTrend } from './MonthTrend'
import { PeriodTrend } from './PeriodTrend'
import { firstOfMonth, lastOfMonth } from './days'
import { monthForecastOf, type MonthForecast, type TrendPoint } from './receiptsDigest'
import { StatCards, type StatItem } from './StatCards'
import './browse.css'

export interface ReceiptsFiltersProps {
  /** Сегодняшний день человека, `ГГГГ-ММ-ДД`. */
  today: string
  /** Период в днях; `null` — период задаёт выбранная смена, границы пусты до выбора дат и виден `periodNote`. */
  period: DayRange | null
  periodNote?: string | null
  onPeriod: (range: DayRange) => void
  /** Самый длинный период, который источник отдаёт за раз; `undefined` — без предела. */
  maxDays?: number
  /** С какого дня начинается «За всё время» (по умолчанию 2000 год). */
  allTimeFrom?: string
  /** Первый день с данными источника: предыдущие месяцы в выборе периода серые (данных нет). */
  dataFrom?: string | null
  /**
   * Итоги по отбору крупными цифрами. Нет — сводки не рисуются: так собрана вкладка «Чеки» (RP-FB74), где суммы за период
   * не показываются; их несёт только «Статистика по продажам».
   */
  stats?: readonly StatItem[]
  statsBusy?: boolean
  /** Сообщения источника между итогами и отбором: ошибка итогов, «показаны не все чеки». */
  notices?: ReactNode
  kinds: readonly ChoiceChip[]
  kind: string
  onKind: (kind: string) => void
  /** Кассиры области отбора; у источника без кассира — только «Все кассиры» (селект виден, но выключен, с пояснением). */
  cashiers: readonly ChoiceChip[]
  cashier: string
  onCashier: (name: string) => void
  /** Почему кассира выбрать нельзя («В 1С кассир чека пока не читается»). */
  cashierHint?: string
  /** Вкладки экрана сразу под кассиром: порядок «кассир → вкладка → период и итоги». */
  tabs?: ReactNode
  /** Постоянные действия рядом с выбором кассира. */
  cashierActions?: ReactNode
  /**
   * Поиск и выбор кассира большого справочника, предоставленный экраном. `null` — кассира у этого экрана нет вовсе (магазин сводит
   * все кассы): блок выбора, его пояснение и подсказка не рисуются.
   */
  cashierControl?: ReactNode
  /**
   * Динамика периода: столбцы по дням или месяцам. Нажатие на столбец открывает его отрезок. Нет ни `trend`, ни `periodTrend`,
   * ни `trendContent` — графика и прогноза нет, дни выбираются обычными кнопками месяца (без сумм).
   */
  trend?: readonly TrendPoint[]
  /** Динамика по месяцам для периода длиннее месяца («Весь год», «За всё время»): столбец месяца открывает этот месяц. */
  periodTrend?: readonly TrendPoint[]
  trendBusy?: boolean
  /** «Обновить» перечитывает только сегодняшний день: загрузка — на его столбце, прошлые дни не исчезают. */
  todayBusy?: boolean
  trendMessage?: string
  /** Альтернативное представление уже загруженного месячного графика (например, по сменам). */
  trendContent?: ReactNode
  trendActions?: ReactNode
  /**
   * Поле поиска есть, только когда на экране список чеков (чеки одного дня загружены). Нет списка — нет поля и нет фразы вместо него;
   * `false` без видов чека — блок отбора не рисуется вовсе.
   */
  searchShown?: boolean
  search: string
  searchLabel: string
  onSearch: (value: string) => void
  /** Поиск применяется кнопкой «Найти» (AQSI: страницы с сервера); без него — на каждый символ (1С: чеки уже на экране). */
  onSearchSubmit?: () => void
  onSearchReset?: () => void
  /** Поиск набран, но ещё не применён. */
  searchPending?: boolean
  days: readonly ActiveDay[]
  /** Дни, в которые по отбору были чеки: открыть день одним нажатием. */
  onDay: (day: string) => void
}

function MonthForecastNote({ forecast }: { forecast: MonthForecast }) {
  return (
    <p className="krp-days__forecast" role="status" aria-label="Среднее и ожидаемая выручка">
      <span className="krp-days__forecast-item">
        <span className="krp-days__forecast-label">Среднее</span>
        <strong className="krp-days__forecast-sum">{forecast.money(forecast.average)}</strong>
      </span>
      <span className="krp-days__forecast-item">
        <span className="krp-days__forecast-label">Ожидаемая выручка</span>
        <strong className="krp-days__forecast-sum">{forecast.money(forecast.expected)}</strong>
      </span>
      <span className="krp-days__forecast-item">
        <span className="krp-days__forecast-label">Текущий месяц</span>
        <strong className="krp-days__forecast-sum">{forecast.money(forecast.monthSum)}</strong>
      </span>
    </p>
  )
}

/**
 * Общие фильтры и итоги чеков — ОДИН набор на AQSI и 1С: период (месяцы года, дни, границы), крупные итоги, вид чека, поиск,
 * кассир над месяцами и месячный график выбора дней. Источник даёт свои данные (`receiptsDigest`) и своё место в иерархии; здесь нет ни 1С, ни AQSI.
 * Кнопка или поле, которых нет у одного источника, — значит, источник не передал их, а не «забыли нарисовать».
 */
export function ReceiptsFilters(props: ReceiptsFiltersProps) {
  const submit = (event: FormEvent) => {
    event.preventDefault()
    props.onSearchSubmit?.()
  }
  const cashierOptions = props.cashiers.map((chip) => ({ value: chip.id, label: chip.count === undefined ? chip.label : `${chip.label} (${chip.count})` }))
  const noCashiers = props.cashiers.length <= 1 && props.cashier === ''
  const longPeriod = props.period !== null && props.period.from !== '' && props.period.to > lastOfMonth(props.period.from)
  const chartShown = props.trend !== undefined || props.periodTrend !== undefined || props.trendContent !== undefined
  const monthShown = props.trendContent === undefined && !(longPeriod && props.periodTrend !== undefined)
  const cashierShown = props.cashierControl !== null
  const searchShown = props.searchShown !== false
  const toolbarShown = searchShown || props.kinds.length > 0
  const forecast = chartShown && monthShown ? monthForecastOf(props.trend ?? [], firstOfMonth(props.period?.from || props.today), props.today) : null
  return (
    <>
      {cashierShown && (
        <div className="krp-receipt-cashier">
          {props.cashierControl ?? <SelectField label="Кассир" value={props.cashier} options={cashierOptions} onChange={props.onCashier} />}
          {props.cashierActions}
          {noCashiers && (
            <p className="krp-receipt-note" role="status">
              {props.cashierHint ?? 'В выбранном отборе кассиров нет или он один.'}
            </p>
          )}
        </div>
      )}
      {props.tabs}
      <DayStrip pager={!chartShown} {...(chartShown ? { daysContent: <>{props.trendActions}{props.trendContent ?? (longPeriod && props.periodTrend !== undefined ? <PeriodTrend points={props.periodTrend} onPick={props.onPeriod} /> : <MonthTrend month={firstOfMonth(props.period?.from || props.today)} today={props.today}
        value={props.period} points={props.trend ?? []} busy={props.trendBusy === true} todayBusy={props.todayBusy === true}
        {...(props.trendMessage !== undefined ? { message: props.trendMessage } : {})} onChange={props.onPeriod} />)}</> } : {})} today={props.today} value={props.period ?? { from: '', to: '' }} onChange={props.onPeriod} {...(forecast !== null ? { customAside: <MonthForecastNote forecast={forecast} /> } : {})} {...(props.maxDays !== undefined ? { maxDays: props.maxDays } : {})} {...(props.allTimeFrom !== undefined ? { allTimeFrom: props.allTimeFrom } : {})} {...(props.dataFrom !== undefined ? { dataFrom: props.dataFrom } : {})} />
      {props.period === null && props.periodNote != null && (
        <p className="krp-receipt-note" role="status">
          <Clock size={16} aria-hidden="true" />
          <span>{props.periodNote}</span>
        </p>
      )}
      {props.stats !== undefined && <StatCards label="Итоги по отбору" items={props.stats} busy={props.statsBusy === true} />}
      {props.notices}
      {toolbarShown && <form className="krp-receipt-toolbar" role="search" aria-label="Отбор чеков" onSubmit={submit}>
        <ChoiceChips label="Вид чека" items={props.kinds} value={props.kind} onChange={props.onKind} />
        {searchShown && (
        <div className="krp-receipt-search">
          <TextField type="search" label={props.searchLabel} value={props.search} onChange={props.onSearch} />
          {props.onSearchSubmit !== undefined && (
            <Button type="submit" variant="secondary" icon={<Search size={16} aria-hidden="true" />}>
              Найти
            </Button>
          )}
          {props.onSearchReset !== undefined && (props.search !== '' || props.searchPending === true) && (
            <Button icon={<X size={16} aria-hidden="true" />} onClick={props.onSearchReset}>
              Сбросить
            </Button>
          )}
        </div>
        )}
      </form>}
      {props.searchPending === true && (
        <p className="krp-receipt-note" role="status">
          Поиск изменён и ещё не применён: список показан по прежнему отбору.
        </p>
      )}
    </>
  )
}
